// ============================================================================
// AFB-0075 / PR 5 — "Generate onboarding packet", the picker.
//
// Two decisions and nothing else: WHICH packet, and WHICH documents FOR THIS
// SEND. It creates a DRAFT envelope and hands it to the review screen; it
// cannot send anything, and nothing it does here changes the packet
// definition — a tick becomes include/exclude on one call, never an edit to
// esign_packet_documents.
//
// The plan is re-fetched on every tick because esign_packet_plan writes
// nothing: the page count, the order and the gaps the sender is looking at are
// always the real answer for the exact list they have chosen, rather than an
// estimate the browser worked out for itself.
//
// Failure renders INSIDE this dialog and nothing unmounts it while it is up.
// The sender's ticks are the only copy of a decision they have made.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, FileText, Loader2, X } from 'lucide-react';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';
import { EsignError } from './lib';
import { envFromPacket, packetPlan } from './packet-lib';
import {
  LaunchPacketOption, PacketPlan, computeDocumentOverride, defaultPacketFor,
  packetsForWorkerClass, planSelectedTemplateIds,
} from './packet-launch';

export interface PacketLaunchModalProps {
  onboardingId: string;
  /** Who this is for — shown so a packet is never generated at the wrong person. */
  personName: string;
  workerClass: string | null;
  options: LaunchPacketOption[];
  onClose: () => void;
  /** Called with the DRAFT envelope. The caller navigates to the review. */
  onGenerated: (envelopeId: string) => void;
}

export function PacketLaunchModal({
  onboardingId, personName, workerClass, options, onClose, onGenerated,
}: PacketLaunchModalProps) {
  const candidates = useMemo(
    () => packetsForWorkerClass(options, workerClass),
    [options, workerClass],
  );
  const [packetId, setPacketId] = useState(
    () => defaultPacketFor(options, workerClass)?.packet_id ?? candidates[0]?.packet_id ?? '',
  );
  // NULL until the first plan lands: until then nobody knows what the rules
  // chose, and a checkbox list drawn from a guess would be a lie.
  const [ruleSelected, setRuleSelected] = useState<string[] | null>(null);
  const [ticked, setTicked] = useState<string[] | null>(null);
  const [plan, setPlan] = useState<PacketPlan | null>(null);
  const [planBusy, setPlanBusy] = useState(true);
  const [planError, setPlanError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const packet = candidates.find((p) => p.packet_id === packetId) ?? null;

  const override = useMemo(
    () => (ruleSelected === null || ticked === null
      ? { include: null, exclude: null }
      : computeDocumentOverride(ruleSelected, ticked)),
    [ruleSelected, ticked],
  );
  const includeKey = (override.include ?? []).join(',');
  const excludeKey = (override.exclude ?? []).join(',');

  // Everything is reset HERE rather than in an effect on packetId: an effect
  // would fire the fetch once with the previous packet's ticks still in state.
  function choosePacket(id: string) {
    setPacketId(id);
    setRuleSelected(null);
    setTicked(null);
    setPlan(null);
    setError(null);
  }

  useEffect(() => {
    if (!packetId) { setPlanBusy(false); return; }
    let cancelled = false;
    setPlanBusy(true);
    setPlanError(null);
    packetPlan({
      packet_id: packetId,
      onboarding_id: onboardingId,
      include: override.include,
      exclude: override.exclude,
    })
      .then((p) => {
        if (cancelled) return;
        setPlan(p);
        // Only the FIRST plan for a packet defines the baseline — later ones
        // are already filtered by the sender's own override.
        setRuleSelected((prev) => prev ?? planSelectedTemplateIds(p));
        setTicked((prev) => prev ?? planSelectedTemplateIds(p));
      })
      .catch((e) => {
        if (!cancelled) {
          setPlanError(errorMessage(e, 'Could not work out what this packet would send.'));
        }
      })
      .finally(() => { if (!cancelled) setPlanBusy(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packetId, onboardingId, includeKey, excludeKey, reloadTick]);

  const planById = useMemo(
    () => new Map((plan?.documents ?? []).map((d) => [d.template_id, d])),
    [plan],
  );
  const rules = useMemo(() => new Set(ruleSelected ?? []), [ruleSelected]);
  const chosen = useMemo(() => new Set(ticked ?? []), [ticked]);

  function toggle(templateId: string) {
    setTicked((prev) => {
      if (prev === null) return prev;
      return prev.includes(templateId)
        ? prev.filter((id) => id !== templateId)
        : [...prev, templateId];
    });
  }

  const gapSentences = plan
    ? plan.gaps.map((g) => g.reason).filter((r) => (r ?? '').trim() !== '')
    : [];
  // An empty document list makes the plan blocking with NOTHING in gaps, so it
  // has to be said here or the button would grey out with no explanation.
  const emptySelection = plan !== null && plan.documents.length === 0;
  const refusals = emptySelection
    ? [
        ...gapSentences,
        'Nothing is ticked, so this packet would send an empty envelope. Tick at ' +
          'least one document.',
      ]
    : gapSentences;
  const bindingGaps = plan?.binding_gaps ?? [];
  const overridden = override.include !== null || override.exclude !== null;
  const canGenerate =
    !busy && !planBusy && planError === null && plan !== null && !plan.blocking &&
    (ticked?.length ?? 0) > 0;

  async function generate() {
    if (!canGenerate) return;
    setBusy(true);
    setError(null);
    try {
      const res = await envFromPacket({
        packet_id: packetId,
        onboarding_id: onboardingId,
        include: override.include,
        exclude: override.exclude,
      });
      // Deliberately leaves `busy` set: the caller navigates to the review and
      // this dialog goes with it. Clearing it would flash a live button on a
      // packet that has already been built.
      onGenerated(res.envelope_id);
    } catch (e) {
      if (e instanceof EsignError && e.code === 'packet_blocked') {
        setError(
          'This packet cannot be built for this person yet — nothing was created. ' +
            'Close the gaps listed here and try again.',
        );
        setReloadTick((n) => n + 1);
      } else {
        setError(errorMessage(e, 'The packet could not be built. Nothing was sent.'));
      }
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4"
      {...overlayClickClose(() => { if (!busy) onClose(); })}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="packet-launch-title"
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:max-w-lg sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="packet-launch-title" className="text-base font-semibold text-tcs-text">
              Generate onboarding packet
            </h2>
            <p className="mt-0.5 text-xs text-tcs-text-muted">
              For {personName}
              {workerClass ? ` · ${workerClass}` : ''}
              {plan?.state ? ` · ${plan.state}` : ' · state not recorded'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => { if (!busy) onClose(); }}
            aria-label="Close"
            className="-mr-2 -mt-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-tcs-text-muted"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        {/* ── which packet ─────────────────────────────────────────────── */}
        <label
          htmlFor="packet-launch-packet"
          className="mt-4 block text-[11px] font-medium text-tcs-text-secondary"
        >
          Packet
        </label>
        <select
          id="packet-launch-packet"
          value={packetId}
          disabled={busy}
          onChange={(e) => choosePacket(e.target.value)}
          className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-2 text-sm text-tcs-text disabled:opacity-50"
        >
          {candidates.map((p) => (
            <option key={p.packet_id} value={p.packet_id}>
              {p.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] text-tcs-text-muted">
          Selected by worker class. The documents below are the ones this
          packet&rsquo;s rules choose for this person — tick to change what goes
          out <strong>this time only</strong>; the packet itself is untouched.
        </p>

        {/* ── the documents ────────────────────────────────────────────── */}
        <div className="mt-3 rounded-md border border-tcs-border">
          {planBusy && ticked === null ? (
            <p className="flex items-center gap-2 p-3 text-xs text-tcs-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Working out what this person would be sent…
            </p>
          ) : (
            <ul className="divide-y divide-tcs-border">
              {(packet?.documents ?? []).map((doc) => {
                const isTicked = chosen.has(doc.template_id);
                const byRules = rules.has(doc.template_id);
                const placed = planById.get(doc.template_id);
                return (
                  <li key={doc.link_id} className="px-3 py-2">
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={isTicked}
                        disabled={busy || ticked === null}
                        onChange={() => toggle(doc.template_id)}
                        className="mt-0.5 h-4 w-4 shrink-0"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-medium text-tcs-text">
                          {doc.name}
                        </span>
                        <span className="block text-[11px] text-tcs-text-muted">
                          {placed
                            ? `${placed.page_count} page${placed.page_count === 1 ? '' : 's'}` +
                              ` · starts on page ${placed.page_offset + 1}`
                            : doc.page_count
                              ? `${doc.page_count} page${doc.page_count === 1 ? '' : 's'}`
                              : 'page count unknown'}
                          {doc.applies_to ? ` · ${doc.applies_to.join('/')} only` : ''}
                          {doc.applies_to_states
                            ? ` · ${doc.applies_to_states.join('/')} only`
                            : ''}
                          {doc.is_required ? '' : ' · optional on this packet'}
                        </span>
                        {isTicked && !byRules && (
                          <span className="mt-0.5 inline-block rounded bg-st-info-bg px-1.5 py-0.5 text-[10px] font-medium text-st-info-fg">
                            added for this send
                          </span>
                        )}
                        {!isTicked && byRules && (
                          <span className="mt-0.5 inline-block rounded bg-st-warn-bg px-1.5 py-0.5 text-[10px] font-medium text-st-warn-fg-strong">
                            removed for this send
                          </span>
                        )}
                        {!isTicked && !byRules && (
                          <span className="mt-0.5 inline-block text-[10px] text-tcs-text-muted">
                            the rules do not select this for this person
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
              {(packet?.documents ?? []).length === 0 && (
                <li className="px-3 py-2 text-[11px] italic text-tcs-text-muted">
                  {packet === null
                    ? 'No active packet covers this person, so there is nothing to pick from.'
                    : 'This packet has no documents on it yet.'}
                </li>
              )}
            </ul>
          )}
        </div>

        {plan && (
          <p className="mt-2 text-[11px] text-tcs-text">
            <strong>
              {plan.documents.length} document{plan.documents.length === 1 ? '' : 's'} ·{' '}
              {plan.total_pages} page{plan.total_pages === 1 ? '' : 's'}
            </strong>
            {overridden && (
              <span className="text-tcs-text-muted">
                {' '}· overridden for this send only
              </span>
            )}
            {planBusy && (
              <span className="text-tcs-text-muted"> · re-checking…</span>
            )}
          </p>
        )}

        {/* ── refusals and gaps, before anything is built ──────────────── */}
        {refusals.length > 0 && (
          <div
            role="alert"
            className="mt-3 rounded-md border border-st-danger-border-strong bg-st-danger-bg p-2"
          >
            <p className="flex items-start gap-1.5 text-[11px] font-semibold text-st-danger-fg-strong">
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
              This packet cannot be built yet
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[11px] text-st-danger-fg-strong">
              {refusals.map((g) => <li key={g}>{g}</li>)}
            </ul>
          </div>
        )}

        {bindingGaps.length > 0 && (
          <div className="mt-2 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-2">
            <p className="text-[11px] font-semibold text-st-warn-fg-strong">
              {bindingGaps.length} value{bindingGaps.length === 1 ? '' : 's'} could not be
              looked up
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[11px] text-st-warn-fg-strong">
              {bindingGaps.map((g) => (
                <li key={`${g.binding_key ?? 'gap'}-${g.reason}`}>{g.reason}</li>
              ))}
            </ul>
            <p className="mt-1 text-[11px] text-st-warn-fg-strong">
              You can fill these in on the review screen. Any of them that a
              document actually needs will block the send until it is filled.
            </p>
          </div>
        )}

        {planError && (
          <p role="alert" className="mt-2 text-xs text-st-danger-fg">{planError}</p>
        )}
        {error && (
          <p role="alert" className="mt-2 text-xs text-st-danger-fg">{error}</p>
        )}

        <p className="mt-3 text-[11px] text-tcs-text-muted">
          This builds a <strong>draft</strong> envelope and opens it for review.
          Nothing is emailed until you send it from there.
        </p>

        <div className="mt-3 flex flex-col gap-2 sm:flex-row-reverse">
          <button
            type="button"
            onClick={generate}
            disabled={!canGenerate}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-md bg-tcs-accent-ink px-4 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
          >
            {busy
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              : <FileText className="h-4 w-4" aria-hidden />}
            {busy ? 'Building…' : 'Build packet for review'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-md border border-tcs-border px-4 text-sm font-medium text-tcs-text disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
